import type { MessageKey } from "@chams-pay/contracts";
import type { StudentFilters } from "./use-students.js";

export interface StudentListSummarySegment {
  readonly labelKey: MessageKey | null;
  readonly valueKey?: MessageKey;
  readonly value?: string;
}

export interface StudentListSummaryModel {
  readonly count: number;
  readonly enrolled: boolean;
  readonly segments: readonly StudentListSummarySegment[];
}

const lifecycleValueKey = {
  active: "sp.statusActive",
  withdrawn: "sp.statusWithdrawn",
  graduated: "sp.statusGraduated",
} as const satisfies Record<Exclude<StudentFilters["lifecycleStatus"], "all">, MessageKey>;

export function buildStudentListSummary({
  activeCount,
  visibleCount,
  query,
  filters,
  filtersActive,
}: {
  readonly activeCount: number;
  readonly visibleCount: number;
  readonly query: string;
  readonly filters: StudentFilters;
  readonly filtersActive: boolean;
}): StudentListSummaryModel {
  const normalizedQuery = query.trim();
  if (!filtersActive && !normalizedQuery) {
    return { count: activeCount, enrolled: true, segments: [] };
  }

  const segments: StudentListSummarySegment[] = [];
  if (normalizedQuery) {
    segments.push({ labelKey: "sp.summarySearch", value: `«${normalizedQuery}»` });
  }
  if (filters.classLabel) {
    segments.push({ labelKey: "sp.summaryLevel", value: filters.classLabel });
  }
  if (filters.lifecycleStatus !== "all") {
    segments.push({
      labelKey: "sp.summaryStatus",
      valueKey: lifecycleValueKey[filters.lifecycleStatus],
    });
  }

  if (filters.arrears === "registration") {
    segments.push({ labelKey: null, valueKey: "sp.summaryRegistrationUnpaid" });
  } else if (filters.arrears === "overdue") {
    segments.push({ labelKey: "sp.summaryPayment", valueKey: "sp.summaryArrears" });
  } else if (filters.arrears === "urgent") {
    segments.push({ labelKey: "sp.summaryLate", valueKey: "sp.summaryTwoPlusMonths" });
  } else if (filters.arrears === "one") {
    segments.push({ labelKey: "sp.summaryLate", valueKey: "dash.oneMonth" });
  } else if (filters.arrears === "two") {
    segments.push({ labelKey: "sp.summaryLate", valueKey: "dash.twoMonths" });
  } else if (filters.arrears === "three-plus") {
    segments.push({ labelKey: "sp.summaryLate", valueKey: "dash.threePlus" });
  } else if (filters.paymentStatus === "up-to-date") {
    segments.push({ labelKey: "sp.summaryPayment", valueKey: "sp.upToDate" });
  } else if (filters.paymentStatus === "one-month-late") {
    segments.push({ labelKey: "sp.summaryLate", valueKey: "dash.oneMonth" });
  } else if (filters.paymentStatus === "two-months-late") {
    segments.push({ labelKey: "sp.summaryLate", valueKey: "dash.twoMonths" });
  } else if (filters.paymentStatus === "three-plus-months-late") {
    segments.push({ labelKey: "sp.summaryLate", valueKey: "dash.threePlus" });
  }

  return { count: visibleCount, enrolled: false, segments };
}
